import { key } from './geometry';
import type { ObstacleMap, Point, Puzzle, Terrain } from './types';

/**
 * The lessons the training yard isolates (GDD §4.6): two primers that teach
 * the verbs — raise a tower, then move one for a better line — and the five
 * properties of the span rule that the daily rewards but never explains.
 *
 * Each family names one thing. A drill belongs to exactly one of them: a
 * layout that teaches two things teaches neither, which is the whole reason
 * these are authored rather than generated.
 */
export type DrillFamily =
  | 'first-light'
  | 'tuning'
  | 'diagonal-tax'
  | 'triangle-trap'
  | 'exact-spacing'
  | 'junction'
  | 'lake-economics';

/**
 * Every drill, by its permanent id.
 *
 * Stable keys, forever: a completed drill is persisted against its id, and
 * `ui/strings.ts` keys each language's name and lesson off the same set, so
 * the type is what keeps a dictionary from drifting out of step with the yard.
 */
export type DrillId =
  | 'first-light'
  | 'tuning-rearrange'
  | 'diagonal-tax-weave'
  | 'triangle-trap-fan'
  | 'triangle-trap-delta'
  | 'exact-spacing-footings'
  | 'junction-neck'
  | 'junction-approach'
  | 'junction-late-split'
  | 'lake-narrows'
  | 'lake-west-arm';

/** One hand-authored lesson: a fixed map, and the one thing it is about. */
export interface Drill {
  /** Which lesson this is. Never renumbered, never reused (see DrillId). */
  id: DrillId;
  family: DrillFamily;
  /**
   * The layout. `seedTowers` is the naive read the drill exists to beat: it
   * lights every house and is measurably worse than the reference, in exactly
   * the way the family names. The first-light primer never shows its seed
   * line; there it exists only to anchor the reference search.
   */
  puzzle: Puzzle;
}

/** Shorthand, so a hand-placed layout reads as a list of coordinates. */
const at = (r: number, c: number): Point => ({ r, c });

/** Paint an inclusive rectangle of terrain into an obstacle map. */
function rect(
  map: ObstacleMap,
  terrain: Terrain,
  r0: number,
  c0: number,
  r1: number,
  c1: number,
): ObstacleMap {
  for (let r = r0; r <= r1; r++) {
    for (let c = c0; c <= c1; c++) map[key(at(r, c))] = terrain;
  }
  return map;
}

/**
 * Buttes and short ridges, clear of the lesson.
 *
 * Not decoration: an empty 9x9 field reads as a canvas rather than a map
 * (STYLEGUIDE §4), and every cell they take out is a cell the reference search
 * never has to try. Terrain that moved a drill's answer would break that
 * drill's own test, which is how these stay honest.
 */
function buttes(list: Point[]): ObstacleMap {
  const map: ObstacleMap = {};
  for (const p of list) map[key(p)] = 'mtn';
  return map;
}

/**
 * The training yard, easiest first.
 *
 * The first-light primer leads, because a player who found the yard before
 * the daily has never placed a tower. After it, families run in the order of
 * the GDD §4.6 table, which is also roughly increasing difficulty: the
 * diagonal tax is one span of arithmetic, lake economics is a whole route.
 * Inside a family, the drill whose gap is easiest to see comes first.
 */
export const DRILLS: readonly Drill[] = [
  {
    id: 'first-light',
    family: 'first-light',
    // Three houses out past span, each one tower from the plant: the verb,
    // three times, with nothing else in frame. The seed line is near the
    // reference on purpose; this drill has nothing to discover (its unit
    // test pins that), only something to do.
    puzzle: {
      plant: at(4, 4),
      houses: [at(1, 1), at(1, 7), at(7, 4)],
      obstacles: buttes([at(8, 0), at(8, 1), at(7, 0), at(8, 8), at(8, 7), at(7, 8)]),
      par: 3,
      seedTowers: [at(2, 2), at(2, 6), at(6, 4)],
      archetype: 'radial',
    },
  },
  {
    id: 'tuning-rearrange',
    family: 'tuning',
    // The other verb, on the map the first primer just taught: the same
    // three arms, now built clumsily — off-axis towers whose top pair also
    // strings a pointless cross-wire. Any single move improves the reading,
    // which is the point: the lesson is that towers move, not where they go.
    puzzle: {
      plant: at(4, 4),
      houses: [at(1, 1), at(1, 7), at(7, 4)],
      obstacles: buttes([at(8, 0), at(8, 1), at(7, 0), at(8, 8), at(8, 7), at(7, 8)]),
      par: 3,
      seedTowers: [at(2, 3), at(2, 5), at(6, 3)],
      archetype: 'radial',
    },
  },
  {
    id: 'diagonal-tax-weave',
    family: 'diagonal-tax',
    puzzle: {
      plant: at(4, 0),
      houses: [at(2, 4), at(6, 4), at(4, 8)],
      obstacles: buttes([at(0, 1), at(0, 2), at(1, 1), at(8, 6), at(8, 7), at(7, 7)]),
      par: 3,
      seedTowers: [at(2, 2), at(4, 4), at(2, 6)],
      archetype: 'radial',
    },
  },
  {
    id: 'triangle-trap-fan',
    family: 'triangle-trap',
    puzzle: {
      plant: at(4, 0),
      houses: [at(2, 4), at(6, 4), at(4, 6)],
      obstacles: buttes([at(0, 7), at(0, 8), at(1, 8), at(8, 7), at(8, 8), at(7, 8)]),
      par: 3,
      seedTowers: [at(3, 2), at(5, 2), at(4, 4)],
      archetype: 'radial',
    },
  },
  {
    id: 'triangle-trap-delta',
    family: 'triangle-trap',
    puzzle: {
      plant: at(4, 0),
      houses: [at(2, 6), at(6, 6), at(4, 7)],
      obstacles: buttes([at(0, 0), at(0, 1), at(1, 0), at(8, 0), at(8, 1), at(7, 0)]),
      par: 4,
      seedTowers: [at(4, 2), at(4, 4), at(3, 6), at(5, 6)],
      archetype: 'radial',
    },
  },
  {
    id: 'exact-spacing-footings',
    family: 'exact-spacing',
    puzzle: {
      plant: at(4, 0),
      houses: [at(2, 8), at(6, 8), at(4, 7)],
      obstacles: rect(rect({}, 'water', 0, 3, 8, 3), 'water', 0, 5, 8, 5),
      par: 4,
      seedTowers: [at(4, 1), at(4, 2), at(4, 4), at(4, 6)],
      archetype: 'delta',
    },
  },
  {
    id: 'junction-neck',
    family: 'junction',
    puzzle: {
      plant: at(4, 0),
      houses: [at(3, 6), at(5, 6), at(4, 8)],
      obstacles: buttes([at(4, 7), at(0, 0), at(0, 1), at(8, 0), at(8, 1)]),
      par: 4,
      seedTowers: [at(4, 2), at(4, 4), at(2, 6), at(6, 6)],
      archetype: 'radial',
    },
  },
  {
    id: 'junction-approach',
    family: 'junction',
    puzzle: {
      plant: at(7, 8),
      houses: [at(3, 2), at(5, 2), at(4, 0)],
      obstacles: buttes([at(4, 1), at(0, 0), at(0, 1), at(0, 8), at(1, 8)]),
      par: 5,
      seedTowers: [at(6, 6), at(5, 4), at(3, 3), at(7, 3), at(5, 1)],
      archetype: 'radial',
    },
  },
  {
    id: 'junction-late-split',
    family: 'junction',
    // No cell reaches all three houses, so the fork is forced and its place
    // is the whole lesson: the seed forks at the plant and pays for two long
    // lines, the reference leaves on one shared trunk and forks late. The
    // unit test measures exactly that, as lines leaving the plant.
    puzzle: {
      plant: at(7, 4),
      houses: [at(1, 2), at(3, 0), at(1, 6)],
      obstacles: buttes([at(8, 0), at(8, 1), at(7, 0), at(8, 8), at(8, 7), at(7, 8)]),
      par: 4,
      seedTowers: [at(5, 2), at(3, 2), at(5, 5), at(3, 6)],
      archetype: 'radial',
    },
  },
  {
    id: 'lake-narrows',
    family: 'lake-economics',
    puzzle: {
      plant: at(1, 1),
      houses: [at(1, 7), at(3, 7), at(5, 7)],
      obstacles: rect(rect({}, 'water', 0, 4, 8, 4), 'water', 2, 5, 4, 5),
      par: 5,
      seedTowers: [at(3, 2), at(5, 3), at(6, 5), at(4, 6), at(2, 6)],
      archetype: 'river',
    },
  },
  {
    id: 'lake-west-arm',
    family: 'lake-economics',
    // A horizontal arm hung from the west edge, ending beside the plant. The
    // naive line misapplies the previous lesson: it marches to the far gate,
    // squeezes over, and doubles back, while the open end sits one column
    // from home. Deliberately the transpose of the retired 'lake-open-end',
    // which wore the crossing drill's own vertical wall — two boards in a
    // row that read as one setup undercut the lesson each was isolating
    // (GDD §4.6: the board is the only place a lesson shows). A changed
    // layout takes a fresh id, never a reused one.
    puzzle: {
      plant: at(1, 7),
      houses: [at(7, 3), at(7, 5), at(7, 7)],
      obstacles: rect(rect({}, 'water', 4, 0, 4, 5), 'water', 5, 2, 5, 4),
      par: 5,
      seedTowers: [at(2, 5), at(3, 3), at(5, 1), at(6, 3), at(6, 5)],
      archetype: 'river',
    },
  },
];

/** Look a drill up by its persistence key. `undefined` for an id we never had. */
export function drillById(id: string): Drill | undefined {
  return DRILLS.find((d) => d.id === id);
}

/**
 * The seed a drill's reference search runs on.
 *
 * A drill has no grid number to seed from, and it reveals its reference the
 * moment it is solved (GDD §4.5), so the layout it reveals has to be the same
 * one on every device. The id is the only thing about a drill that is stable
 * forever, so the seed is derived from it: FNV-1a, 32-bit, which is enough
 * spread for a dozen fixed maps and identical on every engine.
 */
export function drillSeed(id: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < id.length; i++) {
    hash ^= id.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}
